<template>
  <div class="attribution-visite">
    <span v-if="hasUnsyncedData && isAttributed" class="mutation-cloud-icon" title="Données en attente de synchronisation">☁</span>
    <!-- Cas 1: Personne attribuée - afficher nom et agence -->
    <div v-if="isAttributed" class="attributed">
      <p class="bold manager-name">
        <a v-if="canReassign" @click.prevent="handleReassign" class="text-blue-600 hover:text-blue-800 cursor-pointer">
          {{ managerName }}
        </a>
        <span v-else>{{ managerName }}</span>
      </p>
      <p v-if="teamName" class="text-sm text-gray-600">{{ teamName }}</p>
      <p v-if="delegationText" class="text-sm text-gray-500">{{ delegationText }}</p>
    </div>
    
    <!-- Cas 2: Non attribué - afficher bouton "À attribuer" -->
    <div v-else class="not-attributed">
      <button 
        class="action-button bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1 transition-colors"
        @click="handleAttribute"
      >
        À attribuer
        <i class="ico assign ml-1"></i>
      </button>
    </div>
  </div>
</template>

<script>
import dexieService from '@/services/dexieService'
import { openAttributionModal } from '@/utils/attributionHelper'

export default {
  name: 'AttributionVisite',
  props: {
    // Données du gestionnaire technique
    technicalManager: {
      type: Object,
      default: () => null
    },
    // ID de la propriété pour l'attribution
    propertyId: {
      type: String,
      required: true
    },
    // Équipes de la propriété pour calculer la délégation
    propertyTeams: {
      type: Array,
      default: () => []
    },
    // Callback pour l'attribution
    onAttribute: {
      type: Function,
      default: () => () => null
    },
    // Indicateur si la propriété a des données en attente de synchronisation
    hasUnsyncedData: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userFromDexie: null,
      loading: false
    }
  },
  computed: {
    isAttributed() {
      return this.technicalManager && this.technicalManager.id
    },
    
    managerName() {
      // Priorité 1: Données récupérées via Dexie
      if (this.userFromDexie) {
        if (this.userFromDexie.fullName) {
          return this.userFromDexie.fullName
        }
        
        if (this.userFromDexie.firstName && this.userFromDexie.lastName) {
          return `${this.userFromDexie.firstName} ${this.userFromDexie.lastName}`
        }
      }
      
      // Priorité 2: Données du prop technicalManager (fallback)
      if (!this.technicalManager) return ''
      
      if (this.technicalManager.fullName) {
        return this.technicalManager.fullName
      }
      
      if (this.technicalManager.firstName && this.technicalManager.lastName) {
        return `${this.technicalManager.firstName} ${this.technicalManager.lastName}`
      }
      
      return this.technicalManager.name || ''
    },
    
    teamName() {
      // Priorité 1: Données récupérées via Dexie
      if (this.userFromDexie && this.userFromDexie.team) {
        return this.userFromDexie.team.name || '-'
      }
      
      // Priorité 2: Données du prop technicalManager (fallback)
      return this.technicalManager?.team?.name || '-'
    },
    
    delegationText() {
      // Utiliser la fonction de lot.js pour calculer la délégation
      const { getDelegationText } = require('@/utils/lot')
      const managerTeamName = this.userFromDexie?.team?.name || this.technicalManager?.team?.name
      return getDelegationText(this.propertyTeams, managerTeamName)
    },
    
    canReassign() {
      return !!this.technicalManager?.id
    }
  },
  watch: {
    // Surveiller les changements du gestionnaire technique pour recharger les données
    'technicalManager.id': {
      handler(newId) {
        if (newId) {
          this.loadUserFromDexie(newId)
        } else {
          this.userFromDexie = null
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadUserFromDexie(userId) {
      if (!userId) return
      
      try {
        this.loading = true
        
        // Récupérer l'utilisateur depuis la table users de Dexie
        const user = await dexieService.users().get(userId)
        
        if (user) {
          this.userFromDexie = user
        } else {
          console.warn(`Utilisateur avec l'ID ${userId} non trouvé dans Dexie`)
          this.userFromDexie = null
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'utilisateur depuis Dexie:', error)
        this.userFromDexie = null
      } finally {
        this.loading = false
      }
    },
    
    handleAttribute() {
      const attributeFunction = this.onAttribute(this.propertyId)
      if (typeof attributeFunction === 'function') {
        attributeFunction()
      }
    },
    
    handleReassign() {
      // Ouvrir le modal d'attribution avec les données actuelles pré-remplies
      const teamId = this.userFromDexie?.team?.id || this.technicalManager?.team?.id || null
      const managerId = this.technicalManager?.id || null
      
      openAttributionModal(this.$store, {
        lots: [this.propertyId],
        teamId,
        managerId
      })
    }
  }
}
</script>

<style scoped>
@import './AttributionVisite.scss';
</style>
